import { useBilling } from "@/features/billing/api/useBilling";
import { useAreAllNotificationsDisabled } from "@/features/knock/api/preferencesQuery";
import {
    NavNotificationBadge,
    usePatientNotificationCounts,
} from "@/features/onboarding/hooks/usePatientNotificationCounts";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import useMissingProfileItems from "@/features/profile/hooks/useMissingProfileItems";
import { useAuth } from "@/utils/auth";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

const PatientProfileNotificationCount = ({ extra }: { extra: number }) => {
    const { profileBadgeCount } = usePatientNotificationCounts();
    return <NavNotificationBadge count={profileBadgeCount + extra} />;
};

const ProfileNotificationCount = () => {
    const auth = useAuth();
    const isPatient = auth.roles?.isPatient === true;
    const { isLoading: profileLoading } = useQuery(selectProfileByIdQueryOptions(auth.id));
    const { items: missingProfileItems } = useMissingProfileItems();
    const { data: billing, isLoading: billingLoading } = useBilling(auth.id);
    const notificationsDisabled = useAreAllNotificationsDisabled();
    const billingMissing = auth.roles?.isClinician === true && !billingLoading && !billing;
    // A clinician who is also a patient takes the patient path below. Their journey already covers
    // profile completeness, but the profile page still shows the subscription and notifications notices.
    const patientExtra = (notificationsDisabled ? 1 : 0) + (billingMissing ? 1 : 0);

    const staffCount = useMemo(() => {
        let incomplete = missingProfileItems.length;
        if (billingMissing) incomplete++;
        if (notificationsDisabled) incomplete++;
        return incomplete;
    }, [billingMissing, missingProfileItems.length, notificationsDisabled]);

    if (profileLoading) return null;

    if (isPatient) {
        return <PatientProfileNotificationCount extra={patientExtra} />;
    }

    return <NavNotificationBadge count={staffCount} />;
};

export default ProfileNotificationCount;
